import { Map, List, fromJS, is } from 'immutable';

import Actions from 'flow/actions';
import {
    listToMap,
    extensionStatuses,
    keyIn,
    tryParseJsonObject,
} from 'flow/utils';
import { mergeKeyedLists } from 'flow/utils';
import { getVoiceMessagesSortedByReceivedDate } from 'flow/utils/call-records-utils';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import { withAvatarVersion } from 'flow/utils/avatar-utils';

const initialState = Map()
    .set('statuses', Map())
    .set('activeProfiles', Map())
    .set('extensions', Map())
    // Whether the one bulk capabilities fetch has landed. Until it has, an
    // extension without a capabilities map has simply not arrived; afterwards,
    // it is not on the customer at all.
    .set('extensionsCapabilitiesAreLoaded', false);

const getExtensionStatus = (key, state) => {
    const activeCalls = state
        .getIn(['activeLineData', key], Map())
        .filter((item) => !item.get('isOmnichannelErrand'));
    const activeExternalChannels = state.getIn(
        ['activeExternalChannels', key],
        Map(),
    );
    const activeProfile = state.getIn(['activeProfiles', key], Map());

    if (activeProfile && !activeProfile.isEmpty()) {
        // If it's the user's own extension, the state is based on calls
        // and the profile availability is ignored.
        if (activeProfile.get('available', true)) {
            // TEL-1770 added boolean so that lyncOnThePhone will be presented as inCall
            if (activeProfile.get('lyncOnThePhone', false)) {
                return extensionStatuses.inCall.key;
            }

            if (activeCalls && activeCalls.size > 0) {
                const hasUp = activeCalls.some((c) => c.get('state') === 'up');

                if (hasUp) {
                    return extensionStatuses.inCall.key;
                } else {
                    return extensionStatuses.ringingCall.key;
                }
            } else if (
                activeExternalChannels &&
                activeExternalChannels.size > 0
            ) {
                return extensionStatuses.inExternalCall.key;
            } else {
                return extensionStatuses.available.key;
            }
        } else {
            return extensionStatuses.unavailable.key;
        }
    } else {
        return extensionStatuses.unknown.key;
    }
};

const getStatuses = (extensions) => {
    let statuses = Map();
    extensions.forEach((extension) => {
        if (extension.has('state')) {
            statuses = statuses.set(
                extension.get('key'),
                extension.get('state'),
            );
        }
    });
    return statuses;
};

const deleteStatus = (extension) => {
    return extension.delete('state');
};

const deleteContact = (extension) => {
    return extension.delete('contact');
};

const getLyncStatuses = (extensions, activeProfiles) => {
    let statuses = Map();
    extensions.forEach((extension) => {
        if (
            activeProfiles?.getIn(
                [extension.get('key'), 'lyncOnThePhone'],
                false,
            ) === true
        ) {
            statuses = statuses.set(
                extension.get('key'),
                extensionStatuses.inCall.key,
            );
        }
    });
    return statuses;
};

// mergeActiveProfileData is used in RESULT_EXTENSIONS and RESULT_EXTENSION to not remove fields
// that are not present in the new data received from the API.
// It is intentionally so that mergeActiveProfileData is not used in EXTENSION_UPDATE_ACTIVE_PROFILE, as there we expect full profile data is received, so a set is made.
const mergeActiveProfileData = (prevActiveProfile, newActiveProfile) => {
    // If new data is not a valid Map, keep the previous data
    if (!Map.isMap(newActiveProfile)) {
        return prevActiveProfile;
    }

    // If there's no previous data, just use the new data
    if (!Map.isMap(prevActiveProfile)) {
        return newActiveProfile;
    }

    // Merge to preserve fields not present in new data
    // (e.g., lyncOnThePhone, available)
    return prevActiveProfile.merge(newActiveProfile);
};

/**
 * To be used with data from the API as is, not the Redux store.
 * See comment starting right above extensionsReducer for more information.
 */
const getActiveCallsFromExtensionDtos = (extensionMap) => {
    return extensionMap
        .map((extension) => extension.get('activeCalls'))
        .filter((activeCall) => !!activeCall);
};

/**
 * As there is currently something wrong with the parkChannelDTO
 * from the home API, we don't want to overwrite any data that
 * we currently have, for example from TOP.
 * (The number in the parkChannelDTO can miss the first digit, the "0")
 *
 * TODO: When home delivers correctly formatted numbers in
 * the parkChannelDTO, we can remove this.
 */
const removeParkChannelDTO = (oldActiveCalls, apiActiveCalls) => {
    if (!oldActiveCalls || !apiActiveCalls) {
        return apiActiveCalls;
    }

    const getKey = (activeCall) => activeCall.get('key');
    const activeCallsMap = listToMap(oldActiveCalls, getKey);

    return apiActiveCalls.map((activeCall) => {
        const oldActiveCall = activeCallsMap.get(activeCall.get('key'), Map());
        const shouldRemoveParkChannelDTO = oldActiveCall.has('parkChannelDTO');
        return shouldRemoveParkChannelDTO
            ? activeCall.delete('parkChannelDTO')
            : activeCall;
    });
};

// The reducer rebuilds a part from the payload as a new object, even for
// equal data; this keeps the old one. Like updateIn, it creates no entry when
// the path is unset and the updater gives back notSetValue.
const updateInIfChanged = (state, path, notSetValue, updater) => {
    const pathIsSet = state.hasIn(path);
    const prev = state.getIn(path, notSetValue);
    const next = updater(prev);
    const unchanged = pathIsSet ? is(prev, next) : next === notSetValue;
    return unchanged ? state : state.setIn(path, next);
};

const withContactAvatarVersion = (extension, fallbackVersion) =>
    extension.has('contact')
        ? extension.update('contact', (contact) =>
              withAvatarVersion(contact, fallbackVersion),
          )
        : extension;

/**
 * Note: In this reducer you'll see 'activeCalls' being accessed in the data received from the API.
 * In the redux Store, however, we call this activeLineData -- not activeCalls.
 * The reason for this change in naming in the store is that activeLineData can contain
 * sip sessions that represent an omni ticket referring to a whatsapp conversation, i.e. a non-call item.
 */
// eslint-disable-next-line complexity
export const extensionsReducer = (state = initialState, action) => {
    switch (action ? action.type : null) {
        case Actions.CLEAR_STATE: {
            return initialState;
        }
        case Actions.CLEAR_EXTENSIONS_ACTIVE_CALLS: {
            // Top's login sync re-sends only the Teams channels still busy.
            return state
                .delete('activeLineData')
                .delete('activeExternalChannels');
        }
        case Actions.RESULT_EXTENSIONS: {
            const myExtensionKey = action.myExtensionKey;
            const extensionList = fromJS(action.data);
            const extensionMap = listToMap(extensionList, (e) => e.get('key'));
            const prevActiveProfiles = state.get('activeProfiles', Map());
            const prevExtensions = state.get('extensions', Map());

            const aiCoworkerList = extensionList
                ? fromJS(
                      extensionList
                          ?.filter(
                              (e) => e.get('extensionType') === 'ai_coworker',
                          )
                          ?.map((e) =>
                              fromJS({
                                  key: e.get('key'),
                                  description: e.getIn([
                                      'contact',
                                      'firstName',
                                  ]),
                                  number: e.getIn(['contact', 'fixed']),
                              }),
                          ),
                  )
                : List();

            const statuses = getStatuses(extensionMap);

            // Extract activeProfiles from extensions
            const activeProfilesMap = extensionMap
                .map((extension) => extension.get('activeProfile'))
                .filter((profile) => !!profile);

            const lyncStatuses = getLyncStatuses(
                prevExtensions,
                prevActiveProfiles,
            );
            const activeCalls = getActiveCallsFromExtensionDtos(extensionMap);

            // Without lastUpdate the address stays plain, as before, so it
            // stays the same across list fetches.
            const trimmedExtensionMap = extensionMap.map((extension) =>
                withContactAvatarVersion(
                    deleteStatus(extension)
                        .delete('activeCalls')
                        .delete('activeProfile'),
                ),
            );

            return state
                .update('extensions', (prevExtensions) => {
                    /**
                     * Remove the previous contact from the state while retrieving extension information.
                     * Preventing issues with merging when the new value is empty.
                     */
                    if (action.contact) {
                        return prevExtensions.map(deleteContact);
                    }
                    return prevExtensions;
                })
                .update('extensions', (prevExtensions) => {
                    return prevExtensions
                        .filter(
                            keyIn(
                                ...trimmedExtensionMap.keys(),
                                myExtensionKey,
                            ),
                        )
                        .mergeDeep(trimmedExtensionMap);
                })
                .update('activeProfiles', (prevActiveProfiles = Map()) => {
                    return prevActiveProfiles.mergeWith(
                        mergeActiveProfileData,
                        activeProfilesMap,
                    );
                })
                .update('statuses', (prevStatuses) =>
                    (!statuses.isEmpty()
                        ? prevStatuses.filter(
                              keyIn(...statuses.keys(), myExtensionKey),
                          )
                        : prevStatuses
                    ).merge(lyncStatuses),
                )
                .update('activeLineData', (previousData = Map()) => {
                    if (action.live) {
                        return previousData.map(
                            (oldActiveCallList, extensionKey) => {
                                const newActiveCallList =
                                    activeCalls.get(extensionKey);
                                const filteredNewActiveCalls =
                                    removeParkChannelDTO(
                                        oldActiveCallList,
                                        newActiveCallList,
                                    ) || List();

                                /**
                                 * If the new data does not have the activeCalls property - which is currently the case for all users who are not the logged in user -
                                 * we keep the calls in the old list,
                                 */
                                const keepPreviousKeys = !extensionMap.hasIn([
                                    extensionKey,
                                    'activeCalls', //As this is data from the API, not the Redux store, 'activeCalls' is the correct path to access
                                ]);
                                return mergeKeyedLists(
                                    oldActiveCallList,
                                    filteredNewActiveCalls,
                                    keepPreviousKeys,
                                );
                            },
                        );
                    } else {
                        return previousData;
                    }
                })
                .set('aiCoworkersSortingData', aiCoworkerList);
        }

        case Actions.RESULT_EXTENSION: {
            let newExtensionData = fromJS(action.data);
            const activeProfileReceived = newExtensionData.get('activeProfile');
            const activeCallsReceived = newExtensionData.has('activeCalls');
            const newActiveCallList = newExtensionData.get('activeCalls');

            // The calls are stored in activeLineData. Kept in the extension,
            // every call change would replace the extensions map.
            newExtensionData = newExtensionData
                .delete('loggedCalls')
                .delete('voiceMessages')
                .delete('state')
                .delete('activeProfile')
                .delete('activeCalls');

            // Without lastUpdate, opening the page loads the picture again.
            newExtensionData = withContactAvatarVersion(
                newExtensionData,
                Date.now(),
            );
            const prevExtensions = state.get('extensions', Map());
            const prevActiveProfiles = state.get('activeProfiles', Map());

            const lyncStatuses = getLyncStatuses(
                prevExtensions,
                prevActiveProfiles,
            );

            /* Opening a person's page fetches them again, mostly with equal
             * data. Each part of the state keeps its object while it is
             * equal, so selectors over all extensions do not run again. */
            let updatedState = state.mergeIn(['statuses'], lyncStatuses);

            if (activeProfileReceived) {
                updatedState = updateInIfChanged(
                    updatedState,
                    ['activeProfiles', action.key],
                    Map(),
                    (prevActiveProfile) =>
                        mergeActiveProfileData(
                            prevActiveProfile,
                            activeProfileReceived,
                        ),
                );
            }

            updatedState = updateInIfChanged(
                updatedState,
                ['extensions', action.key],
                Map(),
                (extension) =>
                    (newExtensionData.has('contact')
                        ? extension.delete('contact')
                        : extension
                    ).mergeDeep(newExtensionData),
            );

            if (activeCallsReceived) {
                //As this is data from the API, not the Redux store, 'activeCalls' is the correct path to check
                updatedState = updateInIfChanged(
                    updatedState,
                    ['activeLineData', action.key],
                    List(),
                    (storedActiveCallList) => {
                        // The key can hold undefined.
                        const oldActiveCallList =
                            storedActiveCallList || List();
                        const filteredNewActiveCalls =
                            removeParkChannelDTO(
                                oldActiveCallList,
                                newActiveCallList,
                            ) || List();
                        return mergeKeyedLists(
                            oldActiveCallList,
                            filteredNewActiveCalls,
                            false,
                        );
                    },
                );
            }
            return updatedState;
        }
        case Actions.RESULT_VOICE_MESSAGES: {
            const data = fromJS(action.data);
            const mappedVoiceMessages = listToMap(data, (item) =>
                item.get('uniqueId'),
            );
            const orderedMappedVoiceMessages =
                getVoiceMessagesSortedByReceivedDate(mappedVoiceMessages);
            return state.setIn(
                ['extensions', action.key, 'voiceMessages'],
                orderedMappedVoiceMessages,
            );
        }
        case Actions.RESULT_VOICE_MESSAGES_ERROR: {
            return state.setIn(
                ['extensions', action.key, 'voiceMessages'],
                List(),
            );
        }
        case Actions.SET_VOICEMESSAGE_READ: {
            const voiceMessage = state.getIn([
                'extensions',
                action.extensionKey,
                'voiceMessages',
                action.voiceMessageUniqueId,
            ]);
            if (voiceMessage) {
                return state.setIn(
                    [
                        'extensions',
                        action.extensionKey,
                        'voiceMessages',
                        action.voiceMessageUniqueId,
                        'voiceMessageState',
                    ],
                    'read',
                );
            }
            return state;
        }
        case Actions.RESULT_MOBILE_SUBSCRIPTION:
            return state.updateIn(
                ['extensions', action.key, 'mobileSubscription'],
                Map(),
                (prev) => prev.mergeDeep(fromJS(action.data)),
            );
        case Actions.RESULT_SESSION: {
            const status = action.data['state'];
            const activeProfile = action.data['activeProfile'];
            delete action.data['state'];
            delete action.data['loggedCalls'];
            delete action.data['voiceMessages'];
            delete action.data['activeProfile'];
            const extension = fromJS(action.data);
            const key = extension.get('key');

            return state
                .updateIn(['extensions', key], Map(), (prev) =>
                    prev.mergeDeep(extension),
                )
                .setIn(['statuses', key], status)
                .setIn(['activeProfiles', key], fromJS(activeProfile));
        }
        case Actions.RESULT_EXTENSION_CALENDAR: {
            return updateInIfChanged(
                state,
                ['extensions', action.extensionKey, 'calendar'],
                undefined,
                () => fromJS(action.data),
            );
        }
        case Actions.RESULT_EXTENSION_SET_OPENINGHOURS: {
            return state.setIn(
                ['extensions', action.extensionKey, 'calendar', 'openingHours'],
                fromJS(action.data),
            );
        }
        case Actions.RESULT_EXTENSION_ADD_CALENDAR_EVENT: {
            return state.updateIn(
                ['extensions', action.extensionKey, 'calendar', 'events'],
                (events) => events.push(fromJS(action.data)),
            );
        }
        case Actions.RESULT_EXTENSION_UPDATE_CALENDAR_EVENT: {
            return state.updateIn(
                ['extensions', action.extensionKey, 'calendar', 'events'],
                List(),
                (events) => {
                    return events.set(
                        events.findIndex(
                            (e) =>
                                e.get('key') ===
                                action.originalCalendarEventKey,
                        ),
                        fromJS(action.data),
                    );
                },
            );
        }
        case Actions.RESULT_EXTENSION_REMOVE_CALENDAR_EVENT: {
            return state.updateIn(
                ['extensions', action.extensionKey, 'calendar', 'events'],
                (events) => {
                    return events.filter(
                        (e) => e.get('key') !== action.data.key,
                    );
                },
            );
        }
        case Actions.RESULT_VOICEMAILS_DELETE_ALL: {
            return state.setIn(
                ['extensions', action.extensionKey, 'voiceMessages'],
                Map(),
            );
        }
        case Actions.UPDATE_VOICEMESSAGES_AFTER_DELETE: {
            return state.deleteIn([
                'extensions',
                action.extensionKey,
                'voiceMessages',
                action.voiceMessageUniqueId,
            ]);
        }
        case Actions.RESULT_NOTIFICATION_SCHEME:
            return state.updateIn(
                ['extensions', action.extensionKey, 'pushTypes'],
                () => fromJS(action.data),
            );

        case Actions.EXTENSION_UPDATE_ACTIVE_CALL: {
            // Adding key explicitly in case it has not been set already
            state = state.updateIn(
                ['activeLineData', action.extensionKey],
                List(),
                (prev) => {
                    const call = fromJS(action.data);
                    const index = prev.findIndex(
                        (c) => c.get('key') === call.get('key'),
                    );

                    let updatedCall = call;
                    // Below check was added in order to make sure we have the correct start time;
                    // If there is no start time in the data included, and a start time is not already set in the store,
                    // current date is set as start time as we assume these conditions are met only when the call has just been created.
                    if (
                        !call.get('startTime') &&
                        !prev.getIn([index, 'startTime'])
                    ) {
                        updatedCall = updatedCall.set(
                            'startTime',
                            new Date().getTime(),
                        );
                    }

                    if (index === -1) {
                        // If we get a new call with call-id activecall-xxxxxxx.x or
                        // activecall-xxxxxx.x.x, we try to copy the information in
                        // activecall-xxxxxxx or activecall-xxxxxx.x
                        // This happens on answering incoming call, where we get a new call with
                        // a similar key and the old one is suspended
                        const twinMatch = /(activecall-[\d+.?]*)\.\d+$/.exec(
                            call.get('key'),
                        );
                        if (twinMatch) {
                            const originalKey = twinMatch[1];
                            const originalCall = prev.find(
                                (c) => c.get('key') === originalKey,
                            );
                            if (originalCall) {
                                const mergedCall =
                                    originalCall.merge(updatedCall);
                                return prev.set(
                                    prev.indexOf(originalCall),
                                    mergedCall,
                                );
                            }
                        }
                        return prev.push(updatedCall);
                    } else {
                        return prev.update(index, (prev) =>
                            prev.merge(updatedCall),
                        );
                    }
                },
            );

            return state.setIn(
                ['statuses', action.extensionKey],
                getExtensionStatus(action.extensionKey, state),
            );
        }

        case Actions.EXTENSION_UPDATE_ACTIVE_CALL_PARK_DTO: {
            return state.updateIn(
                ['activeLineData', action.extensionKey],
                List(),
                (prev) => {
                    const index = prev.findIndex(
                        (c) => c.get('key') === action.activeCallEntityKey,
                    );

                    if (index === -1) {
                        return prev;
                    } else {
                        return prev.setIn(
                            [index, 'parkChannelDTO'],
                            fromJS(action.data),
                        );
                    }
                },
            );
        }
        case Actions.EXTENSION_UPDATE_ACTIVE_CALL_BAG_VARIABLE: {
            return state.updateIn(
                ['activeLineData', action.extensionKey],
                List(),
                (prev) => {
                    const index = prev.findIndex(
                        (c) => c.get('key') === action.callKey,
                    );
                    if (index === -1) {
                        return prev;
                    } else {
                        addUserPhoneLogEntry(
                            'Setting bag variable ',
                            action.variableName,
                            ' for ',
                            action.extensionKey,
                            ': ',
                            action.variableValue,
                        );
                        return prev.setIn(
                            [index, 'bagVariables', action.variableName],
                            tryParseJsonObject(action.variableValue),
                        );
                    }
                },
            );
        }
        case Actions.EXTENSION_COLISTENING_ADD_LISTENER: {
            return state.updateIn(
                ['activeLineData', action.extensionKey],
                List(),
                (prev) => {
                    const index = prev.findIndex(
                        (c) => c.get('key') === action.callKey,
                    );
                    if (index === -1) {
                        return prev;
                    } else {
                        return prev.setIn(
                            [index, 'colisteners', action.supervisorUniqueId],
                            action.supervisorExtensionKey,
                        );
                    }
                },
            );
        }
        case Actions.EXTENSION_COLISTENING_REMOVE_LISTENER: {
            return state.updateIn(
                ['activeLineData', action.extensionKey],
                List(),
                (prev) => {
                    const index = prev.findIndex(
                        (c) => c.get('key') === action.callKey,
                    );
                    if (index === -1) {
                        return prev;
                    } else {
                        return prev.deleteIn([
                            index,
                            'colisteners',
                            action.supervisorUniqueId,
                        ]);
                    }
                },
            );
        }
        case Actions.EXTENSION_SET_EXTERNAL_CHANNEL: {
            state = state.updateIn(
                ['activeExternalChannels', action.extensionKey],
                Map(),
                (prev) => {
                    return prev.set(action.data.channel, fromJS(action.data));
                },
            );
            return state.setIn(
                ['statuses', action.extensionKey],
                getExtensionStatus(action.extensionKey, state),
            );
        }
        case Actions.EXTENSION_REMOVE_EXTERNAL_CHANNEL: {
            state = state.updateIn(
                ['activeExternalChannels', action.extensionKey],
                Map(),
                (prev) => {
                    return prev.delete(action.data.channel);
                },
            );
            if (
                state
                    .getIn(
                        ['activeExternalChannels', action.extensionKey],
                        Map(),
                    )
                    .isEmpty()
            ) {
                state = state.deleteIn([
                    'activeExternalChannels',
                    action.extensionKey,
                ]);
            }
            return state.setIn(
                ['statuses', action.extensionKey],
                getExtensionStatus(action.extensionKey, state),
            );
        }
        case Actions.EXTENSION_CALL_MERGED_INTO_CONFERENCE: {
            return state.updateIn(
                ['activeLineData', action.extensionKey],
                List(),
                (prev) => {
                    const index = prev.findIndex(
                        (c) => c.get('key') === action.callKey,
                    );
                    if (index === -1) {
                        return prev;
                    } else {
                        return prev.setIn(
                            [index, 'conference'],
                            fromJS({ meetingCode: action.meetingCode }),
                        );
                    }
                },
            );
        }
        case Actions.EXTENSION_ATTENDED_TRANSFER_PENDING: {
            return state.updateIn(
                ['activeLineData', action.extensionKey],
                List(),
                (prev) => {
                    const index = prev.findIndex(
                        (c) => c.get('key') === action.callKey,
                    );
                    if (index === -1) {
                        return prev;
                    } else {
                        const pendingTransfer = fromJS({
                            callKey: action.callKey,
                            startTime: new Date(),
                            number: action.number,
                            transferContact: action.contact,
                        });
                        return prev.setIn(
                            [index, 'pendingTransfer'],
                            pendingTransfer,
                        );
                    }
                },
            );
        }
        case Actions.EXTENSION_ATTENDED_TRANSFER_RESOLVED: {
            return state.updateIn(
                ['activeLineData', action.extensionKey],
                List(),
                (prev) => {
                    const index = prev.findIndex((c) =>
                        c.has('pendingTransfer'),
                    );
                    if (index === -1) {
                        return prev;
                    } else {
                        return prev.deleteIn([index, 'pendingTransfer']);
                    }
                },
            );
        }
        case Actions.EXTENSION_UPDATE_ACTIVE_CALL_PIN_CODE: {
            return state.updateIn(
                ['activeLineData', action.extensionKey],
                List(),
                (prev) => {
                    const index = prev.findIndex(
                        (c) => c.get('key') === action.callKey,
                    );
                    if (index === -1) {
                        return prev;
                    } else {
                        return prev.setIn(
                            [index, 'conference', 'pinCode'],
                            action.pinCode,
                        );
                    }
                },
            );
        }
        case Actions.EXTENSION_REMOVE_ACTIVE_CALL: {
            // Adding key explicitly in case it has not been set already
            state = state.updateIn(
                ['activeLineData', action.extensionKey],
                List(),
                (prev) => {
                    return prev.filter(
                        (call) => call.get('key') !== action.data.key,
                    );
                },
            );
            // If we removed the last call in the above filter, remove the extensionKey entry
            // from the activeCalls map, so that the map doesn't grow indefinitely.
            if (
                state
                    .getIn(['activeLineData', action.extensionKey], List())
                    .isEmpty()
            ) {
                state = state.deleteIn(['activeLineData', action.extensionKey]);
            }
            return state.setIn(
                ['statuses', action.extensionKey],
                getExtensionStatus(action.extensionKey, state),
            );
        }
        case Actions.RESULT_PARK_CALL: {
            return state.setIn(
                ['extensions', action.extensionKey, 'parkedCall'],
                fromJS(action.data),
            );
        }
        case Actions.EXTENSION_UPDATE_ACTIVE_PROFILE: {
            // Adding key explicitly in case it has not been set already
            return state
                .updateIn(['extensions', action.extensionKey], Map(), (prev) =>
                    prev.set('key', action.extensionKey),
                )
                .setIn(
                    ['activeProfiles', action.extensionKey],
                    fromJS(action.data),
                )
                .update((updatedState) =>
                    updatedState.setIn(
                        ['statuses', action.extensionKey],
                        getExtensionStatus(action.extensionKey, updatedState),
                    ),
                );
        }

        case Actions.RESULT_FETCH_PRESENCE_NOTIFICATIONS: {
            const extensionDTOList = fromJS(action.data);
            return updateInIfChanged(
                state,
                ['extensions', action.extensionKey, 'presenceNotifications'],
                undefined,
                () => listToMap(extensionDTOList, (e) => e.get('key')),
            );
        }

        case Actions.RESULT_CREATE_PRESENCE_NOTIFICATION: {
            const extension = fromJS(action.data);
            const newMapEntry = Map().set(extension.get('key'), extension);
            return state.updateIn(
                ['extensions', action.extensionKey, 'presenceNotifications'],
                Map(),
                (prev) => prev.mergeDeep(newMapEntry),
            );
        }
        case Actions.RESULT_CONTACT_AVATAR_UPDATE: {
            if (action.extensionKey) {
                const avatarResource = action.data.avatarResource
                    ? `${action.data.avatarResource}?${Date.now()}`
                    : action.data.avatarResource;
                return state.updateIn(
                    [
                        'extensions',
                        action.extensionKey,
                        'contact',
                        'avatarResource',
                    ],
                    () => fromJS(avatarResource),
                );
            }
            return state;
        }
        case Actions.RESULT_CONTACT_AVATAR_DELETE: {
            if (action.extensionKey) {
                return state.deleteIn([
                    'extensions',
                    action.extensionKey,
                    'contact',
                    'avatarResource',
                ]);
            }
            return state;
        }
        case Actions.RESULT_DELETE_PRESENCE_NOTIFICATION:
            return state.deleteIn([
                'extensions',
                action.extensionKey,
                'presenceNotifications',
                fromJS(action.data).get('key'),
            ]);
        case Actions.RESULT_REMOVE_CONTACT:
            return state.deleteIn(['extensions', action.key]);

        case Actions.RESULT_EXTENSION_MOBILE_STATUS:
            return state.setIn(
                ['extensions', action.extensionKey, 'mobileStatus'],
                fromJS(action.data),
            );
        case Actions.RESULT_EXTENSION_MOBILE_SUBSCRIPTION: {
            return state.setIn(
                ['extensions', action.extensionKey, 'mobileSubscription'],
                fromJS(action.data),
            );
        }
        case Actions.SET_CALL_LIST_FILTER:
            return state.set('callListFilter', action.filter);
        case Actions.RESULT_NEW_PENDING_ESIM: {
            const newPendingEsim = fromJS(action.data);
            return state.updateIn(
                [
                    'extensions',
                    action.extensionKey,
                    'mobileSubscription',
                    'pendingEsims',
                ],
                List(),
                (prev) => prev.push(newPendingEsim),
            );
        }
        case Actions.RESULT_EXTENSIONS_NEXT_SCHEDULED_PROFILES: {
            const nextScheduledProfilesList = fromJS(action.data);
            const nextScheduledProfilesMap = listToMap(
                nextScheduledProfilesList,
                (profile) => profile.get('extensionEntityKey'),
            );
            return state.set('nextScheduledProfiles', nextScheduledProfilesMap);
        }
        case Actions.SET_BAG_VARIABLE_TICKET_ID_ACTIVE_CALL: {
            const activeCallKey = action.activeCallKey;
            const extensionKey = action.extensionKey;
            const ticketId = action.ticketId;
            if (!activeCallKey || !extensionKey || !ticketId) return state;
            if (!state.hasIn(['activeLineData', extensionKey])) {
                return state;
            }
            return state.updateIn(
                ['activeLineData', action.extensionKey],
                (prev) => {
                    const index = prev.findIndex(
                        (data) => data.get('key') === activeCallKey,
                    );
                    if (index === -1) {
                        return prev;
                    } else {
                        return prev.setIn(
                            [index, 'bagVariables', 'telavox.omni.ticket_id'],
                            ticketId,
                        );
                    }
                },
            );
        }
        case Actions.RESULT_EXTENSION_CAPABILITIES: {
            const capabilitiesNamesMap = listToMap(
                fromJS(action.capabilities || []),
                (capability) => capability.get('name'),
            );
            return state.setIn(
                ['extensions', action.key, 'capabilities'],
                capabilitiesNamesMap,
            );
            // Note:
            // The reason the code here in action RESULT_EXTENSION_CAPABILITIES looks a bit different from what it looks
            // like in reducer action below, RESULT_EXTENSIONS_CAPABILITIES (data for multiple extensions), is that the data received differs.
            //
            // Here in RESULT_EXTENSION_CAPABILITIES both name and description for each capability is included in the data received,
            // while in RESULT_EXTENSIONS_CAPABILITIES, only the name of the respective capability is included.
            //
            // Example of what the data received here in RESULT_EXTENSION_CAPABILITIES looks like:
            // [{name: "profiles", description: "Profiler"}, {name: "softphone", description: "Softphone"}, //etc]
            //
            // Example of what the data received in reducer RESULT_EXTENSIONS_CAPABILITIES below looks like:
            // [{capabilities: ["softphone", "chat", "video"], key: "extension-2972836"}, {capabilities: ["tickets", "softphone", "chat", "video"], key: "extension-2972838"}, //etc]
        }
        case Actions.RESULT_EXTENSIONS_CAPABILITIES: {
            return action.data
                .reduce((currentState, extension) => {
                    const capabilitiesMap = listToMap(
                        fromJS(extension.capabilities || []),
                        (capabilityName) => capabilityName,
                    );
                    return currentState.setIn(
                        ['extensions', extension.key, 'capabilities'],
                        capabilitiesMap,
                    );
                }, state)
                .set('extensionsCapabilitiesAreLoaded', true);
        }
        default: {
            return state;
        }
    }
};

export default extensionsReducer;
